import { encodeCacheKey } from 'src/app/pbir/utils/cacheKey';
import { HotCache, HotCacheEntry } from "./hot-cache";
import { logger } from '../../pbir/utils/logger';

interface HashLookupEntry {
  value: any;
  normalizedKey: string;
  hash: number;
  bucket: any;
}

const LOGGER = logger('HashLookup');

export class HashLookup<K, V> {
  private count = 0;
  private buckets = new Map<number, Map<any, V>>();
  private emptyBuckets = 0;
  private nextPruneTime = Date.now();

  constructor(private readonly options: HashLookupOptions<K, V>) { }

  private readonly normalizeKey = this.options.normalizeKey ?? ((k: K) => HashLookup.defaultNormalizeKey(k));
  private readonly hashKey = this.options.hashKey ?? ((k: any) => HashLookup.defaultHashKey(k));
  private readonly isValueExpired = this.options.isValueExpired ?? ((v: V) => false);
  private readonly onRemove = this.options.onRemove ?? ((v: V) => { });

  private readonly hotCacheSize = this.options.hotCacheSize ?? 0;
  private readonly hotCache = this.hotCacheSize > 0 ? new HotCache<any, HashLookupEntry>(this.hotCacheSize) : null;
  private readonly hotCacheUseNormalizedKeys = this.options.hotCacheUseNormalizedKeys ?? false;
  private readonly hotCachePruneCallback = this.hotCache ? ((value) => this.isValueExpired(value)) : null;

  private readonly pruneIntervalMin = this.options.pruneIntervalMin ?? 1000 * 4; // 4s
  private readonly pruneIntervalMax = this.options.pruneIntervalMax ?? 1000 * 8; // 8s

  private readonly logPrefix = `[HashLookup${this.options.debugName ?? ''}]`;

  /**
   * Gets an item from the lookup by its key.
   * If an item is found, but it has expired, it will be removed from the lookup.
   * Returns the found item, or undefined if not found or the item has expired.
   **/
  public get(key: K): V | undefined {
    return this.getEntry(key)?.value;
  }

  /**
   * Gets an item from this lookup by its key.
   * If the item is not found, the provided factory is used to create it.
   * If an item is found, but it has expired, it will be removed from the lookup and replaced with the factory created version.
   * Returns the found or created item.
   **/
  public getOrAdd(key: K, factory: (key?: K, hash?: number, normalizedKeyString?: string) => V): V {
    let entry = this.getEntry(key);
    let value = entry?.value;

    if (value === undefined) {
      value = factory(key, entry.hash, entry.normalizedKey);
      if (value === undefined) {
        throw new Error(`${this.logPrefix} factory() returned undefined.`);
      }

      let bucket = entry.bucket;
      if (bucket === undefined) {
        bucket = new Map<string, V>();
        this.buckets.set(entry.hash, bucket);
        this.emptyBuckets++;
      }

      bucket.set(entry.normalizedKey, value);

      if (bucket.size === 1)
        this.emptyBuckets--;

      entry.bucket = bucket;
      entry.value = value;

      if (this.hotCache) {
        let cacheKey = this.hotCacheUseNormalizedKeys ? entry.normalizedKey : key;
        this.hotCache.add(cacheKey, entry);
      }
    }

    return value;
  }

  /**
   * Removes an entry from this lookup with the specified key.
   * Returns a boolean indicating whether the key was found and removed.
   **/
  public remove(key: K) {
    let entry = this.getEntry(key);

    let { bucket, value } = entry;
    if (bucket && value !== undefined) {
      this.internalRemove(entry);
      return true;
    }
    else {
      LOGGER.warn(`${this.logPrefix}: Value to remove not found.`, { key, value, hash: entry.hash });
      return false;
    }
  }

  /**
   * Removes all entries from this lookup.
   **/
  public clear(callOnRemove = true) {
    if (this.buckets.size === 0)
      return;

    if (callOnRemove) {
      for (let bucket of this.buckets.values()) {
        for (let value of bucket.values()) {
          this.internalOnRemove(value);
        }
      }
    }

    this.buckets.clear();
    this.emptyBuckets = 0;
  }



  private getEntry(key: K): HashLookupEntry {
    this.tryPrune();

    let normalizedKey;
    if (this.hotCache) {
      let hotCacheKey = this.hotCacheUseNormalizedKeys ? (normalizedKey = this.normalizeKey(key)) : key;

      let cached = this.hotCache.tryGet(hotCacheKey);
      if (cached !== undefined)
        return cached;
    }

    normalizedKey ??= this.normalizeKey(key);

    let hash = this.hashKey(normalizedKey);
    let bucket = this.buckets.get(hash);
    let value = bucket?.get(normalizedKey);
    let entry = { normalizedKey, hash, bucket, value };

    if (value !== undefined) {
      if (this.isValueExpired(value)) {
        this.internalRemove(entry);
        value = undefined;
      }
    }

    return entry;
  }

  private internalRemove(entry: HashLookupEntry) {
    this.internalOnRemove(entry.value);

    entry.bucket.delete(entry.normalizedKey);

    if (entry.bucket.size === 0)
      this.emptyBuckets++;
  }

  private internalOnRemove(value: V) {
    try {
      this.onRemove(value);
    }
    catch (err) {
      LOGGER.error(`${this.logPrefix}: Unhandled error thrown when calling onRemove.`);
    }
  }

  private tryPrune() {
    let currentTime = Date.now();
    if (currentTime < this.nextPruneTime)
      return;

    let nextPruneTimeOffset = this.pruneIntervalMin + Math.random() * (this.pruneIntervalMax - this.pruneIntervalMin);
    this.nextPruneTime = currentTime + nextPruneTimeOffset;

    if (this.hotCache) {
      this.hotCache.tryPrune(this.hotCachePruneCallback);
    }

    if (this.emptyBuckets === 0) {
      return;
    }

    let emptyBuckets = this.emptyBuckets;
    let newBuckets = new Map<number, Map<string, V>>();
    let removedBuckets = 0;

    for (let [keyStringHash, bucket] of this.buckets.entries()) {
      if (bucket.size > 0) {
        newBuckets.set(keyStringHash, bucket);
      }
      else {
        emptyBuckets--;
        if (emptyBuckets === 0)
          break;
      }
    }

    this.buckets = newBuckets;
  }

  public static defaultNormalizeKey(key: any) {
    return encodeCacheKey(JSON.stringify(key));
  }

  public static defaultHashKey(str: string) {
    let h1 = 0xdeadbeef;
    let h2 = 0x41c6ce57;

    for (let i = 0, ch; i < str.length; i++) {
      ch = str.charCodeAt(i);
      h1 = Math.imul(h1 ^ ch, 2654435761);
      h2 = Math.imul(h2 ^ ch, 1597334677);
    }

    h1 = Math.imul(h1 ^ (h1 >>> 16), 2246822507);
    h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3266489909);
    h2 = Math.imul(h2 ^ (h2 >>> 16), 2246822507);
    h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3266489909);

    return 4294967296 * (2097151 & h2) + (h1 >>> 0);
  }
}

export interface HashLookupOptions<K, V> {
  normalizeKey?: (k: K) => string;
  hashKey?: (k: string) => number;
  isValueExpired?: (v: V) => boolean;
  onRemove?: (v: V) => void;
  hotCacheSize?: number;
  hotCacheUseNormalizedKeys?: boolean;
  pruneIntervalMin?: number;
  pruneIntervalMax?: number;
  debugName?: string;
}
